// Outbound-only postMessage bridge for partner iframe embeds.
//
// Emits events to window.parent so partner pages can auto-size the iframe,
// react to country selections, and surface errors. There is no inbound command
// channel in v1 — partners reconfigure by re-rendering the iframe with new
// URL params.

// S2: the single canonical owner of the cg-embed protocol's wire literals —
// every sender (this bridge, PublicSupplierWidgetPage.jsx) and every
// receiver (EmbedWidgetPage.jsx's listener, and the standalone snippet it
// generates for F5) must consume SOURCE/EVENTS from here rather than
// hand-rolling the string literals, so the two sides can never drift apart.
const SOURCE = 'cg-embed';

const EVENTS = {
  READY: 'cg-embed:ready',
  RESIZE: 'cg-embed:resize',
  COUNTRY_SELECTED: 'cg-embed:country-selected',
  COST_CALCULATED: 'cg-embed:cost-calculated',
  ERROR: 'cg-embed:error',
};

function resolveTargetOrigin(parentOrigin) {
  if (parentOrigin && /^https?:\/\//i.test(parentOrigin)) {
    return parentOrigin;
  }
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch (e) {
      return '*';
    }
  }
  return '*';
}

function safePost(target, type, payload, targetOrigin) {
  if (!target) return;
  try {
    target.postMessage({ source: SOURCE, type, payload }, targetOrigin);
  } catch (e) {
    // Cross-origin postMessage to a closed/restricted window can throw —
    // partners outside our control should never crash the iframe.
  }
}

export function createPostMessageBridge({ parentOrigin } = {}) {
  if (typeof window === 'undefined' || window.parent === window) {
    return {
      emitReady: () => {},
      emitCountrySelected: () => {},
      emitCostCalculated: () => {},
      emitError: () => {},
      observeResize: () => () => {},
      destroy: () => {},
    };
  }

  const target = window.parent;
  const targetOrigin = resolveTargetOrigin(parentOrigin);

  let resizeObserver = null;
  let lastHeight = 0;

  const emit = (type, payload) => safePost(target, type, payload, targetOrigin);

  return {
    emitReady: () => emit(EVENTS.READY, { version: 1 }),
    emitCountrySelected: (country, costData) =>
      emit(EVENTS.COUNTRY_SELECTED, { country, costData }),
    emitCostCalculated: (summary) => emit(EVENTS.COST_CALCULATED, summary),
    emitError: (code, message) => emit(EVENTS.ERROR, { code, message }),

    observeResize: (element) => {
      if (!element || typeof ResizeObserver === 'undefined') return () => {};
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = Math.ceil(entry.contentRect.height);
          if (height && height !== lastHeight) {
            lastHeight = height;
            emit(EVENTS.RESIZE, { height });
          }
        }
      });
      resizeObserver.observe(element);
      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
      };
    },

    destroy: () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    },
  };
}

export { EVENTS, SOURCE };
